import { useCallback, useMemo, useState } from "react";
import { getCycleFamilies } from "@/lib/care-plan-cycle-colors";
import { flattenStepsWithBranchChildren, isStepTerminallyComplete } from "@/lib/care-plan-cycle-utils";
import createId from "@/lib/create-id";
import type { CarePlanCycleDetail } from "@/types/care-plan";
import { detectCycleNesting } from "./detect-cycle-nesting";
import type { SequenceDraft } from "./sequences/types";
import { type CarePlanContextType, type CarePlanEditor, type CarePlanSize, type CarePlanStep, CarePlanStepStatus, type CycleCreationState, type DetailMode, type Orientation, type SequenceCopyState, StepType, findBranchInTree, findStep, isDecisionResolved, isLinkedMessage, isPatientStep } from "./types";
import { useCarePlanLookupQueries } from "./use-care-plan-lookup-queries";

/**
 * Is this step the kind of "preceding" step whose non-terminal state
 * should block a following branch's appt from being scheduled?
 * MESSAGE steps (including linked reminders/follow-ups) don't count —
 * they auto-complete and don't gate clinician scheduling decisions.
 */
function isBlockingPreceder(step: CarePlanStep): boolean {
  if (isLinkedMessage(step)) return false;
  if (step.type === StepType.MESSAGE) return false;
  if (!isPatientStep(step)) return false;
  return step.status !== CarePlanStepStatus.COMPLETED && step.status !== CarePlanStepStatus.SKIPPED && step.status !== CarePlanStepStatus.MISSED;
}

/**
 * First non-message step in a branch. This is the step that becomes
 * eligible to trigger branch auto-selection when scheduled.
 * Both standalone and linked messages auto-complete and never gate scheduling,
 * so we skip past them to the first "real" step.
 */
function firstBranchStep(steps: CarePlanStep[]): CarePlanStep | undefined {
  return steps.find(s => s.type !== StepType.MESSAGE && !isLinkedMessage(s));
}

/**
 * Is ``candidate`` an appointment that can be scheduled by a clinician?
 * Used both for the normal "next appointment" case and for the first step
 * in an unselected branch at a live decision point.
 */
function isSchedulableAppointment(candidate: CarePlanStep | undefined): boolean {
  return !!candidate && candidate.type === StepType.APPOINTMENT && isPatientStep(candidate) && candidate.status === CarePlanStepStatus.PENDING && candidate.serviceIds.length > 0;
}

/**
 * Collect the IDs of steps that a clinician can hit "Schedule" on right now.
 *
 * Two ways to be schedulable:
 * 1. Current behavior — the first PENDING appointment with services on the
 *    active path (main path, or the selected branch below a resolved decision).
 * 2. New — when an unresolved DECISION has no preceding non-terminal step on
 *    the active path, the first step of each branch (if an appointment) is
 *    also schedulable. Scheduling one auto-selects that branch on the backend.
 */
function collectSchedulableIds(steps: CarePlanStep[]): Set<string> {
  const result = new Set<string>();
  let blockedByPreceder = false;
  for (const step of steps) {
    if (isSchedulableAppointment(step)) {
      result.add(step.id);
      return result;
    }
    if (step.type === StepType.DECISION && step.branches) {
      if (isDecisionResolved(step)) {
        const selected = step.branches.find(b => !!b.selectedDate);
        if (selected) {
          const nested = collectSchedulableIds(selected.steps);
          nested.forEach(id => result.add(id));
          if (result.size > 0) return result;
        }
      } else {
        if (!blockedByPreceder) {
          for (const branch of step.branches) {
            const first = firstBranchStep(branch.steps);
            if (isSchedulableAppointment(first)) result.add(first!.id);
          }
        }
        return result;
      }
    }
    if (!blockedByPreceder && isBlockingPreceder(step)) {
      blockedByPreceder = true;
    }
  }
  return result;
}
interface UseCarePlanContextValueParams {
  steps: CarePlanStep[];
  selectedStepId: string | null;
  selectedStep: CarePlanStep | null;
  expandedStepId: string | null;
  actions: CarePlanContextType["actions"];
  orientation: Orientation;
  detailMode: DetailMode;
  readOnly: boolean;
  isCreating: boolean;
  enableMessages: boolean;
  size: CarePlanSize;
  onScheduleStep?: (step: CarePlanStep) => void;
  editor?: CarePlanEditor;
  cycles?: CarePlanCycleDetail[];
  onCycleCreated?: (config: {
    stepRange: [string, string];
    stepIds: string[];
    passCount: number;
    gapMinutes: number;
    parentCycleId?: string | null;
    childCycleIds?: string[];
    branchId?: string | null;
  }) => void;
  onRenameCycle?: (cycleId: string, name: string) => void;
  /** Pass for patient care plans so merge-field previews can use real PATIENT suggestions. */
  patientId?: string;
}
export function useCarePlanContextValue({
  steps,
  selectedStepId,
  selectedStep,
  expandedStepId,
  actions,
  orientation,
  detailMode,
  readOnly,
  isCreating,
  enableMessages,
  size,
  onScheduleStep,
  editor,
  cycles,
  onCycleCreated,
  onRenameCycle,
  patientId
}: UseCarePlanContextValueParams): CarePlanContextType {
  const {
    services,
    medicationOptions,
    measurementOptions,
    isLookupsLoading,
    lookupError
  } = useCarePlanLookupQueries();
  const serviceById = useMemo(() => new Map(services.map(s => [s.id, s])), [services]);
  const medicationById = useMemo(() => new Map(medicationOptions.map(o => [o.value, o])), [medicationOptions]);
  const measurementById = useMemo(() => new Map(measurementOptions.map(o_0 => [o_0.value, o_0])), [measurementOptions]);
  const schedulableStepIds = useMemo(() => collectSchedulableIds(steps), [steps]);
  const getNextStep = useCallback((stepList: CarePlanStep[], index: number) => stepList.at(index + 1), []);
  const [createCycleOpen, setCreateCycleOpen] = useState(false);
  const [cycleCreation, setCycleCreation] = useState<CycleCreationState | null>(null);
  const [sequenceCopy, setSequenceCopy] = useState<SequenceCopyState | null>(null);
  const [highlightedCycleId, setHighlightedCycleId] = useState<string | null>(null);
  const {
    displayCycles,
    familyMap: cycleFamilyMap,
    cycleToDisplayIndex,
    cycleToParentPass
  } = useMemo(() => getCycleFamilies(cycles ?? [], steps), [cycles, steps]);

  // cycleColorMap uses the family-aware display index so clones share colors
  const cycleColorMap = useMemo(() => cycleToDisplayIndex, [cycleToDisplayIndex]);
  const [onlyShowCurrentPasses, setOnlyShowCurrentPasses] = useState(true);
  const toggleOnlyShowCurrentPasses = useCallback(() => setOnlyShowCurrentPasses(v => !v), []);
  const isOriginalStep = useCallback((s_0: CarePlanStep) => {
    const stepAny = s_0 as CarePlanStep & {
      sourceStepId?: string | null;
    };
    return !stepAny.sourceStepId && !isLinkedMessage(s_0);
  }, []);

  // Original (non-materialized) steps for cycle selection.
  // Dynamically scoped to the branch when creating a cycle inside one.
  // Uses findBranchInTree to support any nesting depth.
  const originalSteps = useMemo(() => {
    if (cycleCreation?.branchId) {
      const result = findBranchInTree(steps, cycleCreation.branchId);
      if (result) return result.branch.steps.filter(isOriginalStep);
    }
    return steps.filter(isOriginalStep);
  }, [steps, cycleCreation?.branchId, isOriginalStep]);

  /**
   * Find which branch (if any) a step belongs to.
   * Searches recursively through nested branches at any depth.
   * Returns the branch ID or null for top-level steps.
   */
  const findStepBranch = useCallback((stepId: string): string | null => {
    const search = (stepList_0: CarePlanStep[]): string | null => {
      for (const step of stepList_0) {
        if (step.type === StepType.DECISION && step.branches) {
          for (const branch of step.branches) {
            if (branch.steps.some(s_1 => s_1.id === stepId)) {
              return branch.id;
            }
            const nested = search(branch.steps);
            if (nested) return nested;
          }
        }
      }
      return null;
    };
    return search(steps);
  }, [steps]);
  const openCreateCycle = useCallback(() => {
    setCreateCycleOpen(true);
    setCycleCreation({
      phase: "selecting-start",
      startStepIndex: null,
      startStepId: null,
      endStepIndex: null,
      endStepId: null,
      branchId: null
    });
  }, []);
  const closeCreateCycle = useCallback(() => {
    setCreateCycleOpen(false);
    setCycleCreation(null);
  }, []);
  const cancelCycleCreation = useCallback(() => {
    setCreateCycleOpen(false);
    setCycleCreation(null);
  }, []);
  const selectCycleCreationStep = useCallback((stepId_0: string) => {
    setCycleCreation(prev => {
      if (!prev) return prev;
      if (prev.phase === "selecting-start") {
        // Determine which branch this step is in
        const branchId = findStepBranch(stepId_0);

        // Compute the correct step list for index lookup
        let stepsForIndex: CarePlanStep[];
        if (branchId) {
          const result_0 = findBranchInTree(steps, branchId);
          stepsForIndex = result_0 ? result_0.branch.steps.filter(isOriginalStep) : [];
        } else {
          stepsForIndex = steps.filter(isOriginalStep);
        }
        const stepIndex = stepsForIndex.findIndex(s_2 => s_2.id === stepId_0);
        if (stepIndex === -1) return prev;
        return {
          ...prev,
          phase: "selecting-end",
          startStepIndex: stepIndex,
          startStepId: stepId_0,
          branchId
        };
      }
      if (prev.phase === "selecting-end") {
        // Use originalSteps which is already scoped to the correct branch
        const stepIndex_0 = originalSteps.findIndex(s_3 => s_3.id === stepId_0);
        if (stepIndex_0 === -1) return prev;
        if (prev.startStepIndex !== null && stepIndex_0 < prev.startStepIndex) return prev;
        return {
          ...prev,
          phase: "configuring",
          endStepIndex: stepIndex_0,
          endStepId: stepId_0
        };
      }
      return prev;
    });
  }, [steps, originalSteps, findStepBranch, isOriginalStep]);
  const startSequenceCopy = useCallback((sourceStepId: string) => {
    setCycleCreation(null);
    setSequenceCopy({
      sourceStepId,
      targetStepIds: []
    });
  }, []);
  const cancelSequenceCopy = useCallback(() => {
    setSequenceCopy(null);
  }, []);
  const toggleSequenceCopyTarget = useCallback((stepId_1: string) => {
    setSequenceCopy(prev_0 => {
      if (!prev_0) return prev_0;
      if (stepId_1 === prev_0.sourceStepId) return prev_0;
      const has = prev_0.targetStepIds.includes(stepId_1);
      return {
        ...prev_0,
        targetStepIds: has ? prev_0.targetStepIds.filter(id => id !== stepId_1) : [...prev_0.targetStepIds, stepId_1]
      };
    });
  }, []);
  const applySequenceCopy = useCallback(() => {
    if (!sequenceCopy || sequenceCopy.targetStepIds.length === 0) {
      setSequenceCopy(null);
      return;
    }
    const source = findStep(steps, sequenceCopy.sourceStepId);
    const sourceDrafts = source?.sequences ?? [];
    for (const targetId of sequenceCopy.targetStepIds) {
      const cloned: SequenceDraft[] = sourceDrafts.map(d => ({
        ...d,
        key: createId("seq"),
        id: undefined
      }));
      actions.updateStep(targetId, {
        sequences: cloned,
        sequencesDirty: true
      });
    }
    setSequenceCopy(null);
  }, [sequenceCopy, steps, actions]);
  const confirmCycleCreation = useCallback((passCount: number, gapMinutes: number) => {
    if (!cycleCreation || cycleCreation.startStepIndex === null || cycleCreation.endStepIndex === null) return;
    const startIdx = cycleCreation.startStepIndex;
    const endIdx = cycleCreation.endStepIndex;
    const allStepsForDetection = cycleCreation.branchId ? findBranchInTree(steps, cycleCreation.branchId)?.branch.steps ?? steps : steps;
    const {
      parentCycleId,
      childCycleIds
    } = detectCycleNesting({
      startIdx,
      endIdx,
      originalSteps,
      allSteps: allStepsForDetection,
      cycles
    });
    const capturedStepIds = originalSteps.slice(startIdx, endIdx + 1).map(s_4 => s_4.id);
    onCycleCreated?.({
      stepRange: [cycleCreation.startStepId!, cycleCreation.endStepId!],
      stepIds: capturedStepIds,
      passCount,
      gapMinutes,
      parentCycleId,
      childCycleIds: childCycleIds.length > 0 ? childCycleIds : undefined,
      branchId: cycleCreation.branchId
    });
    setCreateCycleOpen(false);
    setCycleCreation(null);
  }, [cycleCreation, cycles, steps, originalSteps, onCycleCreated]);

  // When onlyShowCurrentPasses is on, filter out steps from completed/skipped
  // passes — keeping only the first incomplete pass per logical cycle.
  // Cloned cycles (from parent-cycle materialization) share a family via
  // templateCycleId and are treated as one logical cycle.
  const visibleSteps = useMemo(() => {
    if (!onlyShowCurrentPasses || !cycles || cycles.length === 0) return steps;

    // Flatten steps so branch children of decision steps are included
    // when determining pass completion.
    const allSteps = flattenStepsWithBranchChildren(steps);

    // Build pass completion status: "cycleId:pass" → allDone
    const passStatus = new Map<string, boolean>();
    for (const step_0 of allSteps) {
      if (!isPatientStep(step_0) || !step_0.cycleId || !step_0.cyclePass) continue;
      const key = `${step_0.cycleId}:${step_0.cyclePass}`;
      const done = isStepTerminallyComplete(step_0);
      const prev_1 = passStatus.get(key);
      passStatus.set(key, prev_1 === undefined ? done : prev_1 && done);
    }

    // For each cycle family, collect all (cycleId, pass) iterations ordered
    // by earliest step position, then find the first incomplete one.
    // visibleIterations: set of "cycleId:pass" keys to show
    const visibleIterations = new Set<string>();
    const processedFamilies = new Set<string>();
    for (const cycle of cycles) {
      // Use family representative to avoid processing the same family twice
      const familyIds = cycleFamilyMap.get(cycle.id);
      const familyKey = familyIds ? Array.from(familyIds).sort().join(",") : cycle.id;
      if (processedFamilies.has(familyKey)) continue;
      processedFamilies.add(familyKey);
      const memberIds = familyIds ?? new Set([cycle.id]);

      // Collect all (cycleId, pass) iterations for this family,
      // ordered by the earliest stepNumber in each iteration
      const iterations: {
        cycleId: string;
        pass: number;
        minStep: number;
      }[] = [];
      const iterationMinStep = new Map<string, number>();
      for (const step_1 of allSteps) {
        if (!isPatientStep(step_1) || !step_1.cycleId || !step_1.cyclePass) continue;
        if (!memberIds.has(step_1.cycleId)) continue;
        const key_0 = `${step_1.cycleId}:${step_1.cyclePass}`;
        const prev_2 = iterationMinStep.get(key_0);
        if (prev_2 === undefined || step_1.stepNumber < prev_2) {
          iterationMinStep.set(key_0, step_1.stepNumber);
        }
      }
      for (const [key_1, minStep] of Array.from(iterationMinStep.entries())) {
        const [cid, passStr] = key_1.split(":");
        iterations.push({
          cycleId: cid,
          pass: Number(passStr),
          minStep
        });
      }
      iterations.sort((a, b) => a.minStep - b.minStep);

      // Find first incomplete iteration
      let found = false;
      for (const iter of iterations) {
        const key_2 = `${iter.cycleId}:${iter.pass}`;
        const allDone = passStatus.get(key_2) ?? false;
        if (!allDone) {
          visibleIterations.add(key_2);
          found = true;
          break;
        }
      }
      // All done — show the first iteration (pass 1 of original)
      if (!found && iterations.length > 0) {
        visibleIterations.add(`${iterations[0].cycleId}:${iterations[0].pass}`);
      }
    }
    return steps.filter(step_2 => {
      if (!isPatientStep(step_2) || !step_2.cycleId || !step_2.cyclePass) return true;
      return visibleIterations.has(`${step_2.cycleId}:${step_2.cyclePass}`);
    });
  }, [steps, onlyShowCurrentPasses, cycles, cycleFamilyMap]);
  return {
    state: {
      steps,
      visibleSteps,
      selectedStepId,
      selectedStep,
      expandedStepId
    },
    actions,
    getNextStep,
    config: {
      orientation,
      detailMode,
      readOnly,
      isCreating,
      enableMessages,
      size
    },
    lookups: {
      services,
      medicationOptions,
      measurementOptions,
      patientId,
      serviceById,
      medicationById,
      measurementById,
      isLookupsLoading,
      lookupError,
      onScheduleStep,
      schedulableStepIds
    },
    cycles: {
      cycles: cycles ?? [],
      createCycleOpen,
      openCreateCycle,
      closeCreateCycle,
      onCycleCreated,
      onRenameCycle,
      cycleCreation,
      selectCycleCreationStep,
      cancelCycleCreation,
      confirmCycleCreation,
      originalSteps,
      highlightedCycleId,
      setHighlightedCycleId,
      onlyShowCurrentPasses,
      toggleOnlyShowCurrentPasses,
      cycleColorMap,
      cycleToParentPass,
      displayCycles,
      cycleFamilyMap
    },
    sequenceCopy: {
      state: sequenceCopy,
      start: startSequenceCopy,
      toggleTarget: toggleSequenceCopyTarget,
      apply: applySequenceCopy,
      cancel: cancelSequenceCopy
    },
    editor
  };
}